import { Coordinates } from '../../../../types';
import { generateCacheKey } from '../utils/roadDetection';
import { WIZARD_CONSTANTS } from '../types/constants/constants';

interface GPSLocationSuggestion {
  /** Suggested text for the road/location field */
  suggestion: string;
  /** Whether this is a predefined road or reverse geocoded address */
  type: 'road' | 'address';
  /** Source of the suggestion for display purposes */
  source: 'gps-road' | 'gps-address';
  /** Distance to the road if type is 'road' */
  distance?: number;
}

// Cache for location suggestions
const suggestionCache = new Map<string, GPSLocationSuggestion>();
const cacheTimestamps = new Map<string, number>();

export const gpsDetectionService = {

  /**
   * Get cached suggestion if available and not expired
   */
  getCachedSuggestion: (coordinates: Coordinates): GPSLocationSuggestion | null => {
    const cacheKey = generateCacheKey(coordinates);
    const cachedSuggestion = suggestionCache.get(cacheKey);
    const cacheTime = cacheTimestamps.get(cacheKey);

    if (
      cachedSuggestion &&
      cacheTime &&
      Date.now() - cacheTime < WIZARD_CONSTANTS.GPS_DETECTION_CACHE_EXPIRY
    ) {
      return cachedSuggestion;
    }

    return null;
  },

  /**
   * Cache a suggestion for given coordinates
   */
  cacheSuggestion: (coordinates: Coordinates, suggestion: GPSLocationSuggestion): void => {
    const cacheKey = generateCacheKey(coordinates);
    suggestionCache.set(cacheKey, suggestion);
    cacheTimestamps.set(cacheKey, Date.now());
  },

  /**
   * Clear expired cache entries
   */
  cleanupCache: (): void => {
    const now = Date.now();
    for (const [key, timestamp] of cacheTimestamps.entries()) {
      if (now - timestamp > WIZARD_CONSTANTS.GPS_DETECTION_CACHE_EXPIRY) {
        suggestionCache.delete(key);
        cacheTimestamps.delete(key);
      }
    }
  },

  /**
   * Create a road suggestion object
   */
  createRoadSuggestion: (roadName: string, distance: number): GPSLocationSuggestion => ({
    suggestion: roadName,
    type: 'road',
    source: 'gps-road',
    distance,
  }),

  /**
   * Create an address suggestion object
   */
  createAddressSuggestion: (address: string): GPSLocationSuggestion => ({
    suggestion: address,
    type: 'address',
    source: 'gps-address',
  }),
};

export type { GPSLocationSuggestion };